import { RATING_DISPLAY_OFFSET, RATING_DISPLAY_SCALE } from '@topmeeps/shared'

// A small Gaussian "you are here" bell over the rating axis.
//
// μ and σ come from the OpenSkill state in raw units. We render in
// display units (raw μ × scale + offset on the X axis) so the curve
// aligns with the scaled SR numbers users see elsewhere. The bell's
// peak is at the displayed rating; its width is the system's
// uncertainty about the player's true skill.

export type RatingGaussianProps = {
  // Raw OpenSkill mu (typical default 25, drifts roughly 15–30).
  mu: number
  // Raw OpenSkill sigma (default ≈8.33, stabilizes around 1.5–2.5).
  sigma: number
  // Display-space domain to render across, e.g. [400, 700]. If omitted,
  // auto-fits to ±3.5σ around μ in display space (good for solo cards).
  domain?: readonly [number, number]
  // Pixel dimensions. Defaults match the small-card aesthetic.
  width?: number
  height?: number
  // Color of the bell fill. Defaults to felt.
  fill?: string
}

const SAMPLES = 64

const displayMu = (mu: number) => mu * RATING_DISPLAY_SCALE + RATING_DISPLAY_OFFSET
const displaySigma = (sigma: number) => sigma * RATING_DISPLAY_SCALE

// ±3.5σ around μ in display space, rounded to clean 25-SR steps and
// clamped to [0, ∞). The lower-tail clamp keeps the axis from showing
// negative SR labels when a high-σ provisional player's bell stretches
// below zero — purely cosmetic, the path itself still renders truthfully.
export const ratingDomain = (mu: number, sigma: number): readonly [number, number] => {
  const m = displayMu(mu)
  const s = displaySigma(sigma)
  return [
    Math.max(0, Math.floor((m - 3.5 * s) / 25) * 25),
    Math.ceil((m + 3.5 * s) / 25) * 25,
  ] as const
}

// Standard normal PDF, unnormalized — we scale heights to fit the box anyway.
const pdf = (x: number, mu: number, sigma: number): number => {
  const z = (x - mu) / sigma
  return Math.exp(-0.5 * z * z)
}

export const RatingGaussian = ({
  mu,
  sigma,
  domain,
  width = 200,
  height = 56,
  fill = 'var(--felt)',
}: RatingGaussianProps) => {
  const dMu = displayMu(mu)
  const dSigma = displaySigma(sigma)

  const [xMin, xMax] = domain ?? ratingDomain(mu, sigma)
  const xSpan = xMax - xMin

  // Sample the PDF and normalize y to [0, 1] over our render window.
  const points: Array<readonly [number, number]> = []
  let yMaxInWindow = 0
  for (let i = 0; i < SAMPLES; i++) {
    const t = i / (SAMPLES - 1)
    const x = xMin + t * xSpan
    const y = pdf(x, dMu, dSigma)
    points.push([x, y] as const)
    if (y > yMaxInWindow) yMaxInWindow = y
  }
  const peak = pdf(dMu, dMu, dSigma)
  // Self-normalized: peak fills the box. If μ is outside the domain
  // (rare, e.g. solo card with a tight axis), the bell is shorter than
  // full height — that's the right read.
  const yScale = Math.min(yMaxInWindow / peak, 1)

  const toPx = (x: number, y: number) => {
    const px = ((x - xMin) / xSpan) * width
    const py = height - (y / peak) * yScale * (height - 4) - 2
    return [px, py] as const
  }

  // Build a closed area path so the bell has a fill.
  const baseY = height - 2
  let d = `M 0 ${baseY}`
  for (const [x, y] of points) {
    const [px, py] = toPx(x, y)
    d += ` L ${px.toFixed(2)} ${py.toFixed(2)}`
  }
  d += ` L ${width} ${baseY} Z`

  return (
    <svg
      // Fluid: scale to whatever horizontal space the parent gives us,
      // up to the prop's width. Height stays fixed so the bell may end
      // up taller-relative-to-wider on narrow screens.
      width="100%"
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      aria-hidden
      style={{ display: 'block', maxWidth: width }}
    >
      <path d={d} fill={fill} fillOpacity={0.25} stroke={fill} strokeWidth={1.25} />
      {/* Explicit baseline — the path's Z-close gets visually nibbled
          by anti-aliasing at the right viewport edge, so draw it. */}
      <line
        x1={0}
        x2={width}
        y1={baseY}
        y2={baseY}
        stroke={fill}
        strokeWidth={1.25}
        strokeLinecap="square"
      />
    </svg>
  )
}
